import apiClient from './client';
import type {
  TaskTemplateWithDetails,
  TemplateType,
  CreateTaskTemplateRequest,
  UpdateTaskTemplateRequest,
  PhaseWithSubGroups,
} from '../../types';

/**
 * API for managing task templates (admin only)
 */
export const taskTemplatesApi = {
  /**
   * Get all task templates with optional filtering
   */
  getAll: async (filters?: {
    cohort_type?: string;
    phase?: string;
    template_type?: TemplateType;
    is_active?: boolean;
    parent_only?: boolean;
  }): Promise<TaskTemplateWithDetails[]> => {
    const params = new URLSearchParams();
    if (filters?.cohort_type) params.set('cohort_type', filters.cohort_type);
    if (filters?.phase) params.set('phase', filters.phase);
    if (filters?.template_type) params.set('template_type', filters.template_type);
    if (filters?.is_active !== undefined) params.set('is_active', String(filters.is_active));
    if (filters?.parent_only !== undefined) params.set('parent_only', String(filters.parent_only));

    const queryString = params.toString();
    return apiClient.get<TaskTemplateWithDetails[]>(
      `/api/admin/task-templates${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get distinct cohort types
   */
  getCohortTypes: async (): Promise<string[]> => {
    return apiClient.get<string[]>('/api/admin/task-templates/cohort-types');
  },

  /**
   * Get available template types
   */
  getTemplateTypes: async (): Promise<TemplateType[]> => {
    return apiClient.get<TemplateType[]>('/api/admin/task-templates/template-types');
  },

  /**
   * Get template counts by type
   */
  getCountsByType: async (): Promise<Record<TemplateType, number>> => {
    return apiClient.get<Record<TemplateType, number>>('/api/admin/task-templates/counts');
  },

  /**
   * Get a single task template by ID
   */
  getById: async (id: string): Promise<TaskTemplateWithDetails> => {
    return apiClient.get<TaskTemplateWithDetails>(`/api/admin/task-templates/${id}`);
  },

  /**
   * Get subtask templates for a parent template
   */
  getSubtasks: async (parentId: string): Promise<TaskTemplateWithDetails[]> => {
    return apiClient.get<TaskTemplateWithDetails[]>(`/api/admin/task-templates/${parentId}/subtasks`);
  },

  /**
   * Create a new task template
   */
  create: async (data: CreateTaskTemplateRequest): Promise<TaskTemplateWithDetails> => {
    return apiClient.post<TaskTemplateWithDetails>('/api/admin/task-templates', data);
  },

  /**
   * Update a task template
   */
  update: async (
    id: string,
    data: UpdateTaskTemplateRequest
  ): Promise<TaskTemplateWithDetails> => {
    return apiClient.put<TaskTemplateWithDetails>(`/api/admin/task-templates/${id}`, data);
  },

  /**
   * Delete a task template
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/admin/task-templates/${id}`);
  },

  /**
   * Duplicate a task template
   */
  duplicate: async (id: string): Promise<TaskTemplateWithDetails> => {
    return apiClient.post<TaskTemplateWithDetails>(`/api/admin/task-templates/${id}/duplicate`);
  },

  /**
   * Get templates grouped by phase and sub-group
   */
  getGrouped: async (
    templateType: TemplateType,
    includeArchived?: boolean
  ): Promise<PhaseWithSubGroups[]> => {
    const params = new URLSearchParams();
    params.set('template_type', templateType);
    if (includeArchived) params.set('include_archived', 'true');
    return apiClient.get<PhaseWithSubGroups[]>(
      `/api/admin/task-templates/grouped?${params.toString()}`
    );
  },

  /**
   * Archive a task template
   */
  archive: async (id: string): Promise<{ archived: boolean }> => {
    return apiClient.post<{ archived: boolean }>(`/api/admin/task-templates/${id}/archive`);
  },

  /**
   * Restore an archived task template
   */
  restore: async (id: string): Promise<{ restored: boolean }> => {
    return apiClient.post<{ restored: boolean }>(`/api/admin/task-templates/${id}/restore`);
  },
};

export default taskTemplatesApi;
